import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { Condition } from 'client/voladeum/conditions/types';
import { Indicator } from 'client/voladeum/indicators/types';
import { Strategy } from 'client/voladeum/strategies/types';
import { Trigger } from 'client/voladeum/triggers/types';

import { strategy_context_initial_state } from './defaults';
import { StrategyContextProps } from './types';

const Context = createContext<StrategyContextProps>({ ...strategy_context_initial_state });

export const StrategyContext: FC<PropsWithChildren> = ({ children }) => {
  const [context, setContext] = useState<StrategyContextProps>({
    ...strategy_context_initial_state,
  });

  const setStrategy = useCallback((strategy: Strategy) => {
    const { indicators } = strategy;
    setContext((prev) => ({ ...prev, strategy, indicators }));
  }, []);

  const updateStrategy = useCallback((strategy: Partial<Strategy>) => {
    setContext((prev) => ({ ...prev, strategy: { ...prev.strategy, ...strategy } }));
  }, []);

  const updateIndicators = useCallback((indicator: Indicator) => {
    setContext((prev) => {
      const indicators = [...prev.indicators];
      const index = indicators.findIndex((i) => i.uuid === indicator.uuid);
      if (index === -1) {
        indicators.push(indicator);
      } else {
        indicators.splice(index, 1, indicator);
      }
      return { ...prev, indicators };
    });
  }, []);

  const updateStrategyTrigger = useCallback((trigger: Trigger) => {
    setContext((prev) => {
      const triggers = [...prev.strategy.triggers];
      const index = triggers.findIndex((t) => t.uuid === trigger.uuid);

      if (index !== -1) {
        triggers.splice(index, 1, trigger);
      } else {
        triggers.push(trigger);
      }

      return { ...prev, strategy: { ...prev.strategy, triggers } };
    });
  }, []);

  const removeStrategyTrigger = useCallback((uuid: string) => {
    setContext((prev) => {
      return {
        ...prev,
        strategy: {
          ...prev.strategy,
          triggers: prev.strategy.triggers.filter((trigger) => trigger.uuid !== uuid),
        },
      };
    });
  }, []);

  const setCondition = useCallback((condition: Condition) => {
    setContext((prev) => {
      return { ...prev, condition };
    });
  }, []);

  const updateCondition = useCallback((condition: Partial<Condition>) => {
    setContext((prev) => {
      return { ...prev, condition: { ...prev.condition, ...condition } };
    });
  }, []);

  const addStrategyTriggerCondition = useCallback((condition: Condition) => {
    setContext((prev) => {
      return {
        ...prev,
        strategy: {
          ...prev.strategy,
          triggers: prev.strategy.triggers.map((trigger) => {
            if (trigger.trigger_id !== condition.trigger_id) return trigger;
            return {
              ...trigger,
              conditions: [...trigger.conditions, condition],
            };
          }),
        },
      };
    });
  }, []);

  const removeStrategyTriggerCondition = useCallback(({ trigger_id, uuid }: Condition) => {
    setContext((prev) => {
      return {
        ...prev,
        strategy: {
          ...prev.strategy,
          triggers: prev.strategy.triggers.map((trigger) => {
            if (trigger.trigger_id !== trigger_id) return trigger;
            return {
              ...trigger,
              conditions: trigger.conditions.filter((condition) => condition.uuid !== uuid),
            };
          }),
        },
      };
    });
  }, []);

  const updateStrategyTriggerCondition = useCallback((condition: Partial<Condition>) => {
    setContext((prev) => {
      return {
        ...prev,
        strategy: {
          ...prev.strategy,
          triggers: prev.strategy.triggers.map((trigger) => {
            if (trigger.trigger_id !== condition.trigger_id) return trigger;
            return {
              ...trigger,
              conditions: trigger.conditions.map(({ uuid, ...rest }) => {
                if (uuid === condition.uuid) return { ...rest, ...condition, uuid };
                return { ...rest, uuid };
              }),
            };
          }),
        },
      };
    });
  }, []);

  const replaceStrategyTriggerCondition = useCallback((condition: Condition) => {
    setContext((prev) => {
      return {
        ...prev,
        strategy: {
          ...prev.strategy,
          triggers: prev.strategy.triggers.map((trigger) => {
            if (trigger.trigger_id !== condition.trigger_id) return trigger;
            return {
              ...trigger,
              conditions: trigger.conditions.map(({ uuid, ...rest }) => {
                if (uuid === condition.uuid) return condition;
                return { ...rest, uuid };
              }),
            };
          }),
        },
      };
    });
  }, []);

  const value = useMemo(
    () => ({
      ...context,
      setCondition,
      updateCondition,
      setStrategy,
      updateStrategy,
      updateIndicators,
      updateStrategyTrigger,
      removeStrategyTrigger,
      updateStrategyTriggerCondition,
      removeStrategyTriggerCondition,
      addStrategyTriggerCondition,
      replaceStrategyTriggerCondition,
    }),
    [
      context,
      setCondition,
      updateCondition,
      setStrategy,
      updateIndicators,
      updateStrategy,
      updateStrategyTrigger,
      removeStrategyTrigger,
      updateStrategyTriggerCondition,
      removeStrategyTriggerCondition,
      addStrategyTriggerCondition,
      replaceStrategyTriggerCondition,
    ]
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useStrategyContext = () => useContext(Context);
